/**
 * AppError 階層 (Sprint BF Part 4、docs/11 §2 エラー仕様の実装側基盤)。
 *
 * 旧 v1 はサービス層で `throw new Error('...')` の生文字列が混在しており、
 * client 側の toast 表示も `(err as Error).message` を素通しするだけだった。
 * v2 では:
 *
 *   - **コード分類** (`code` field) で I18N / 統計 / フォローアップ操作の根拠を持つ
 *   - **callable error 統合** (`toHttpsErrorPayload`) で server → client 間の throw が
 *     code + message + details を構造的に保つ (旧は HttpsError.details 未使用)
 *   - **toJSON** で structured logging (Cloud Logging severity + jsonPayload) に
 *     乗せやすい形に正規化
 *
 * このモジュールは **依存無し** (Firebase SDK / Node のいずれにも依存しない) で
 * shared-types に置く。Firebase HttpsError との橋渡しは呼出側 (functions or
 * client lib) で `toHttpsErrorPayload(err)` を介して行う想定 (循環依存回避)。
 *
 * 階層図 (Part 4 完了条件):
 *
 *   AppError (base, code, message, cause?, details?)
 *     ├─ ValidationError       — 入力 field 不正 (code 'validation/...', HttpsError 'invalid-argument')
 *     ├─ PermissionError       — role 不足 / Custom Claims 不足 (HttpsError 'permission-denied')
 *     ├─ NotFoundError         — Firestore doc 不在 (HttpsError 'not-found')
 *     ├─ RateLimitError        — rate limit 超過 (HttpsError 'resource-exhausted')
 *     ├─ ExternalServiceError  — Gemini / Storage 等の外部障害 (HttpsError 'internal')
 *     └─ NetworkError          — client 側 Firebase offline / fetch 失敗 (HttpsError 'unavailable')
 *
 * 設計メモ:
 *   - cause の chain は `Error.prototype.cause` (ES2022) を採用、互換は target=es2022 で確保
 *   - `instanceof AppError` で全 sub-class が拾える (multi-class catch 不要)
 *   - sub-class は `code` プレフィックスで自己分類 (`validation/...`, `permission/...` 等)
 *   - error message は **end-user 向け** 日本語を許容 (UI の toast にそのまま出る)、
 *     詳細な debug 情報は `details` (任意 record) に格納
 */
/**
 * AppError 基底クラス。
 *
 * `code` は `<category>/<reason>` 形式の安定識別子。UI 側は `instanceof` で大分類、
 * `code` で細分類を判定する。
 */
export class AppError extends Error {
    /** 階層識別 (`validation/title-too-long`, `permission/admin-required` 等)。 */
    code;
    /** 任意のデバッグ情報 (Cloud Logging / HttpsError.details に出力)。 */
    details;
    /** 既定の HttpsError code (sub-class で上書き、生 AppError は 'unknown')。 */
    httpsCode;
    constructor(code, message, options = {}) {
        // ES2022 cause option を採用 (TS target=es2022 で型認識される)。
        super(message, options.cause !== undefined ? { cause: options.cause } : undefined);
        this.name = new.target.name;
        this.code = code;
        this.details = options.details ?? {};
        this.httpsCode = options.httpsCode ?? 'unknown';
        // Node + browser の subclass で prototype chain が壊れないように明示設定
        // (ES5 down-emit の TS / Babel 互換の保険)。
        Object.setPrototypeOf(this, new.target.prototype);
    }
    /** structured logging 用 JSON 化 (cause を再帰展開)。 */
    toJSON() {
        const json = {
            name: this.name,
            code: this.code,
            message: this.message,
        };
        if (Object.keys(this.details).length > 0) {
            json.details = this.details;
        }
        const causeRaw = this.cause;
        if (causeRaw !== undefined) {
            if (causeRaw instanceof AppError) {
                json.cause = causeRaw.toJSON();
            }
            else if (causeRaw instanceof Error) {
                json.cause = { name: causeRaw.name, message: causeRaw.message };
            }
            else {
                json.cause = { name: 'unknown', message: String(causeRaw) };
            }
        }
        return json;
    }
}
/** 入力検証エラー (必須項目欠落 / 長さ超過 / 形式不正等)。 */
export class ValidationError extends AppError {
    constructor(code, message, options = {}) {
        super(code, message, { ...options, httpsCode: 'invalid-argument' });
    }
}
/** 認可エラー (Custom Claims 不足 / role 不一致等、認証は通っている)。 */
export class PermissionError extends AppError {
    constructor(code, message, options = {}) {
        super(code, message, { ...options, httpsCode: 'permission-denied' });
    }
}
/** 対象不在 (Firestore doc がない、Storage object がない等)。 */
export class NotFoundError extends AppError {
    constructor(code, message, options = {}) {
        super(code, message, { ...options, httpsCode: 'not-found' });
    }
}
/** Rate limit 超過 (callable 内 enforceRateLimit / client 側 throttle)。 */
export class RateLimitError extends AppError {
    constructor(code, message, options = {}) {
        super(code, message, { ...options, httpsCode: 'resource-exhausted' });
    }
}
/** 外部サービス障害 (Gemini API / Storage / Drive API 等の 5xx, 429 等)。 */
export class ExternalServiceError extends AppError {
    constructor(code, message, options = {}) {
        super(code, message, { ...options, httpsCode: 'internal' });
    }
}
/** Network / Firebase offline (client 側で fetch 失敗 / offline 中の write 拒否)。 */
export class NetworkError extends AppError {
    constructor(code, message, options = {}) {
        super(code, message, { ...options, httpsCode: 'unavailable' });
    }
}
/* ===== Conversion helpers ===== */
/**
 * AppError → HttpsError 展開用 payload。
 *
 * functions/triggers 側で `throw new HttpsError(p.httpsCode, p.message, p.details)` の形で
 * 投げる (shared-types は firebase-functions に依存しないため payload までを生成)。
 */
export function toHttpsErrorPayload(err) {
    return {
        httpsCode: err.httpsCode,
        message: err.message,
        details: {
            code: err.code,
            ...err.details,
        },
    };
}
/**
 * 任意 error を AppError に正規化する (client 側 catch の入口で使用)。
 *
 *   - 既に AppError なら素通し
 *   - Firebase の HttpsError-like (`{ code, message }`) なら最適な sub-class に変換
 *   - その他 Error は generic AppError('unknown/wrapped', ...) で包む
 *   - 非 Error は String 化して generic AppError に
 *
 * Firebase SDK のエラー code prefix (`functions/...`, `auth/...`) を見て最適な
 * 階層に振り分ける (これにより UI 側で `instanceof PermissionError` 等の判定が効く)。
 */
export function fromUnknownError(raw) {
    if (raw instanceof AppError)
        return raw;
    if (raw instanceof Error) {
        const code = raw.code;
        const codeStr = typeof code === 'string' ? code : '';
        const message = raw.message || codeStr || 'エラーが発生しました';
        // Firebase Functions callable の HttpsError-like
        if (codeStr.includes('permission-denied') || codeStr.includes('auth/insufficient-permission')) {
            return new PermissionError(`permission/${codeStr}`, message, { cause: raw });
        }
        if (codeStr.includes('not-found')) {
            return new NotFoundError(`not-found/${codeStr}`, message, { cause: raw });
        }
        if (codeStr.includes('resource-exhausted')) {
            return new RateLimitError(`rate-limit/${codeStr}`, message, { cause: raw });
        }
        if (codeStr.includes('invalid-argument') || codeStr.includes('failed-precondition')) {
            return new ValidationError(`validation/${codeStr}`, message, { cause: raw });
        }
        if (codeStr.includes('unavailable') || codeStr.includes('network')) {
            return new NetworkError(`network/${codeStr}`, message, { cause: raw });
        }
        if (codeStr.includes('internal') || codeStr.includes('unknown')) {
            return new ExternalServiceError(`external/${codeStr}`, message, { cause: raw });
        }
        return new AppError(codeStr ? `unknown/${codeStr}` : 'unknown/wrapped', message, {
            cause: raw,
        });
    }
    return new AppError('unknown/non-error', String(raw));
}
/**
 * AppError → end-user 向け toast message 抽出。
 *
 * UI 側で `toast('error', formatErrorForToast(fromUnknownError(e)))` のように使う。
 * code prefix で軽い prefix 文字列を付け、生 message が空のときの fallback を提供。
 */
export function formatErrorForToast(err) {
    if (err instanceof RateLimitError)
        return `[制限] ${err.message}`;
    if (err instanceof PermissionError)
        return `[権限不足] ${err.message}`;
    if (err instanceof NotFoundError)
        return `[未検出] ${err.message}`;
    if (err instanceof NetworkError)
        return `[通信] ${err.message}`;
    if (err instanceof ExternalServiceError)
        return `[外部障害] ${err.message}`;
    if (err instanceof ValidationError)
        return err.message;
    return err.message || 'エラーが発生しました';
}
//# sourceMappingURL=errors.js.map